/**
 * VendorHTML - HTML overlay vendor/shop system.
 * Replaces the canvas-based VendorUI with a DOM-based approach.
 * CSS injected once, DOM built on open(), removed on close().
 */

import { NPC, NPCType } from '@client/entities/NPC';
import { Inventory } from '@client/inventory/Inventory';
import { GameItem, getItemColor, ConsumableItem } from '@shared/items/Item';
import { EquipmentItem, ItemRarity, EquipmentSlot, ItemMod } from '@shared/types';
import { t } from '@locales/index';

// ========================
// Types
// ========================

interface ShopSlot {
  item: GameItem;
  price: number;
}

interface BuybackSlot {
  item: GameItem;
  price: number;
}

interface FeedbackMessage {
  text: string;
  color: string;
  timer: number;
}

// ========================
// Pricing
// ========================

const RARITY_BUY_MULTIPLIER: Record<ItemRarity, number> = {
  normal: 10,
  magic: 25,
  rare: 60,
  set: 150,
  legendary: 500,
};

const SELL_RATIO = 0.3;

function calculateBuyPrice(item: GameItem): number {
  const level = 'level' in item ? item.level : 1;
  const rarity = item.rarity as ItemRarity;
  const multiplier = RARITY_BUY_MULTIPLIER[rarity] || 10;
  return Math.max(1, Math.floor(level * multiplier));
}

function calculateSellPrice(item: GameItem): number {
  return Math.max(1, Math.floor(calculateBuyPrice(item) * SELL_RATIO));
}

// ========================
// Shop Item Generation
// ========================

let shopItemCounter = 0;
function generateShopItemId(): string {
  return `shop_${Date.now()}_${shopItemCounter++}`;
}

// Turkish item name components
const SHOP_PREFIXES: Record<string, string[]> = {
  maxHp: ['Canlı', 'Dirençli', 'Dayanıklı'],
  maxMana: ['Ruhani', 'Mistik', 'Büyülü'],
  armor: ['Sert', 'Zırhlı', 'Korumalı'],
  damage: ['Keskin', 'Ölümcül', 'Güçlü'],
  energyShield: ['Parlak', 'Işıklı'],
};

const SHOP_SUFFIXES: Record<string, string[]> = {
  strength: ['Gücün', 'Kuvvetin'],
  agility: ['Çevikliğin', 'Hızın'],
  spirit: ['Ruhun', 'Bilgeliğin'],
  critChance: ['Öfkenin', 'Gazabın'],
  moveSpeed: ['Koşucunun', 'Avcının'],
  attackSpeed: ['Savaşçının'],
  allResist: ['Koruyucunun'],
};

// Base items by slot type
const WEAPON_BASES: { name: string; slot: EquipmentSlot; implicitType: string; implicitValue: number }[] = [
  { name: 'Kılıç', slot: 'weapon', implicitType: 'damage', implicitValue: 10 },
  { name: 'Balta', slot: 'weapon', implicitType: 'damage', implicitValue: 15 },
  { name: 'Yay', slot: 'weapon', implicitType: 'damage', implicitValue: 8 },
  { name: 'Asa', slot: 'weapon', implicitType: 'spellDamage', implicitValue: 15 },
  { name: 'Uzun Kılıç', slot: 'weapon', implicitType: 'damage', implicitValue: 25 },
  { name: 'Savaş Baltası', slot: 'weapon', implicitType: 'damage', implicitValue: 35 },
];

const ARMOR_BASES: { name: string; slot: EquipmentSlot; implicitType: string; implicitValue: number }[] = [
  { name: 'Deri Zırh', slot: 'chest', implicitType: 'armor', implicitValue: 15 },
  { name: 'Demir Zırh', slot: 'chest', implicitType: 'armor', implicitValue: 30 },
  { name: 'Çelik Zırh', slot: 'chest', implicitType: 'armor', implicitValue: 60 },
  { name: 'Börk', slot: 'head', implicitType: 'evasion', implicitValue: 15 },
  { name: 'Demir Tolgası', slot: 'head', implicitType: 'armor', implicitValue: 15 },
  { name: 'Eldiven', slot: 'gloves', implicitType: 'attackSpeed', implicitValue: 3 },
  { name: 'Çizme', slot: 'boots', implicitType: 'moveSpeed', implicitValue: 5 },
  { name: 'Kemer', slot: 'belt', implicitType: 'maxHp', implicitValue: 20 },
  { name: 'Yün Pelerin', slot: 'cape', implicitType: 'armor', implicitValue: 5 },
];

const ACCESSORY_BASES: { name: string; slot: EquipmentSlot; implicitType: string; implicitValue: number }[] = [
  { name: 'Muska', slot: 'amulet', implicitType: 'allResist', implicitValue: 5 },
  { name: 'Yüzük', slot: 'ring1', implicitType: 'strength', implicitValue: 3 },
  { name: 'Gümüş Yüzük', slot: 'ring1', implicitType: 'spirit', implicitValue: 3 },
  { name: 'Kemer', slot: 'belt', implicitType: 'maxHp', implicitValue: 20 },
];

const PREFIX_POOL: ItemMod[] = [
  { type: 'maxHp', value: 20, tier: 1 },
  { type: 'maxHp', value: 35, tier: 2 },
  { type: 'maxMana', value: 15, tier: 1 },
  { type: 'maxMana', value: 25, tier: 2 },
  { type: 'armor', value: 15, tier: 1 },
  { type: 'armor', value: 30, tier: 2 },
  { type: 'damage', value: 5, tier: 1 },
  { type: 'damage', value: 10, tier: 2 },
  { type: 'energyShield', value: 15, tier: 1 },
];

const SUFFIX_POOL: ItemMod[] = [
  { type: 'strength', value: 3, tier: 1 },
  { type: 'strength', value: 6, tier: 2 },
  { type: 'agility', value: 3, tier: 1 },
  { type: 'agility', value: 6, tier: 2 },
  { type: 'spirit', value: 3, tier: 1 },
  { type: 'spirit', value: 6, tier: 2 },
  { type: 'critChance', value: 10, tier: 1, calcType: 'increased' },
  { type: 'moveSpeed', value: 5, tier: 1, calcType: 'increased' },
  { type: 'attackSpeed', value: 5, tier: 1, calcType: 'increased' },
  { type: 'allResist', value: 5, tier: 2 },
];

function rollRarity(): ItemRarity {
  const roll = Math.random();
  if (roll < 0.03) return 'rare';
  if (roll < 0.15) return 'rare';
  if (roll < 0.40) return 'magic';
  return 'normal';
}

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function generateShopEquipment(
  level: number,
  slotFilter?: EquipmentSlot[]
): EquipmentItem {
  let bases: { name: string; slot: EquipmentSlot; implicitType: string; implicitValue: number }[];
  if (slotFilter && slotFilter.length > 0) {
    const allBases = [...WEAPON_BASES, ...ARMOR_BASES, ...ACCESSORY_BASES];
    bases = allBases.filter(b => slotFilter.includes(b.slot));
    if (bases.length === 0) bases = allBases;
  } else {
    bases = [...WEAPON_BASES, ...ARMOR_BASES, ...ACCESSORY_BASES];
  }

  const base = pickRandom(bases);
  const rarity = rollRarity();
  const itemLevel = Math.max(1, level + Math.floor(Math.random() * 5) - 2);

  const levelScale = 1 + (itemLevel - 1) * 0.1;
  const scaledImplicit = Math.floor(base.implicitValue * levelScale);

  const item: EquipmentItem = {
    id: generateShopItemId(),
    name: base.name,
    baseName: base.name,
    slot: base.slot,
    level: itemLevel,
    rarity,
    implicit: [{ type: base.implicitType, value: scaledImplicit, tier: Math.min(3, Math.ceil(itemLevel / 10)) }],
    prefixes: [],
    suffixes: [],
    enhancement: 0,
  };

  let prefixCount = 0;
  let suffixCount = 0;
  switch (rarity) {
    case 'magic':
      prefixCount = 1;
      suffixCount = Math.random() < 0.5 ? 1 : 0;
      break;
    case 'rare':
      prefixCount = 1 + Math.floor(Math.random() * 2);
      suffixCount = 1 + Math.floor(Math.random() * 2);
      break;
    case 'set':
      prefixCount = 2 + Math.floor(Math.random() * 2);
      suffixCount = 1 + Math.floor(Math.random() * 2);
      break;
  }

  const availPrefixes = PREFIX_POOL.filter(m => m.tier <= Math.ceil(itemLevel / 10) + 1);
  const availSuffixes = SUFFIX_POOL.filter(m => m.tier <= Math.ceil(itemLevel / 10) + 1);

  const usedPrefixTypes: Set<string> = new Set();
  for (let i = 0; i < prefixCount && availPrefixes.length > 0; i++) {
    const candidates = availPrefixes.filter(m => !usedPrefixTypes.has(m.type));
    if (candidates.length === 0) break;
    const mod = { ...pickRandom(candidates) };
    mod.value = Math.floor(mod.value * (0.8 + Math.random() * 0.4));
    item.prefixes.push(mod);
    usedPrefixTypes.add(mod.type);
  }

  const usedSuffixTypes: Set<string> = new Set();
  for (let i = 0; i < suffixCount && availSuffixes.length > 0; i++) {
    const candidates = availSuffixes.filter(m => !usedSuffixTypes.has(m.type));
    if (candidates.length === 0) break;
    const mod = { ...pickRandom(candidates) };
    mod.value = Math.floor(mod.value * (0.8 + Math.random() * 0.4));
    item.suffixes.push(mod);
    usedSuffixTypes.add(mod.type);
  }

  if (rarity !== 'normal') {
    let prefix = '';
    let suffix = '';
    if (item.prefixes.length > 0) {
      const modType = item.prefixes[0].type;
      const names = SHOP_PREFIXES[modType];
      if (names) prefix = pickRandom(names) + ' ';
    }
    if (item.suffixes.length > 0) {
      const modType = item.suffixes[0].type;
      const names = SHOP_SUFFIXES[modType];
      if (names) suffix = ' ' + pickRandom(names);
    }
    item.name = `${prefix}${base.name}${suffix}`;
  }

  return item;
}

function generateConsumable(level: number): ConsumableItem {
  const potions: { name: string; effect: 'heal' | 'mana' | 'buff'; baseValue: number }[] = [
    { name: 'Sağlık İksiri', effect: 'heal', baseValue: 50 },
    { name: 'Büyük Sağlık İksiri', effect: 'heal', baseValue: 120 },
    { name: 'Mana İksiri', effect: 'mana', baseValue: 40 },
    { name: 'Büyük Mana İksiri', effect: 'mana', baseValue: 100 },
    { name: 'Canlanma İksiri', effect: 'heal', baseValue: 200 },
    { name: 'Güç İksiri', effect: 'buff', baseValue: 15 },
    { name: 'Çeviklik İksiri', effect: 'buff', baseValue: 15 },
    { name: 'Ruh İksiri', effect: 'buff', baseValue: 15 },
  ];

  const pot = pickRandom(potions);
  const scaledValue = Math.floor(pot.baseValue * (1 + (level - 1) * 0.15));

  return {
    id: generateShopItemId(),
    name: pot.name,
    rarity: 'normal',
    level: level,
    itemType: 'consumable',
    effect: pot.effect,
    value: scaledValue,
    duration: pot.effect === 'buff' ? 30 : undefined,
  };
}

function generateShopInventory(npcType: NPCType, playerLevel: number): ShopSlot[] {
  const slots: ShopSlot[] = [];
  const itemCount = 5 + Math.floor(Math.random() * 4); // 5-8 items

  switch (npcType) {
    case 'demirci': {
      const weaponSlots: EquipmentSlot[] = ['weapon'];
      const armorSlots: EquipmentSlot[] = ['chest', 'head', 'gloves', 'boots', 'belt', 'cape'];
      for (let i = 0; i < itemCount; i++) {
        const filter = i < Math.ceil(itemCount / 2) ? weaponSlots : armorSlots;
        const item = generateShopEquipment(playerLevel, filter);
        slots.push({ item, price: calculateBuyPrice(item) });
      }
      break;
    }

    case 'tacir': {
      const accessorySlots: EquipmentSlot[] = ['amulet', 'ring1', 'ring2', 'belt', 'cape'];
      for (let i = 0; i < itemCount; i++) {
        if (i < Math.ceil(itemCount * 0.6)) {
          const item = generateShopEquipment(playerLevel, accessorySlots);
          slots.push({ item, price: calculateBuyPrice(item) });
        } else {
          const item = generateShopEquipment(playerLevel);
          slots.push({ item, price: calculateBuyPrice(item) });
        }
      }
      break;
    }

    case 'otaci': {
      for (let i = 0; i < itemCount; i++) {
        const item = generateConsumable(playerLevel);
        slots.push({ item, price: calculateBuyPrice(item) });
      }
      break;
    }

    default: {
      for (let i = 0; i < itemCount; i++) {
        const item = generateShopEquipment(playerLevel);
        slots.push({ item, price: calculateBuyPrice(item) });
      }
      break;
    }
  }

  return slots;
}

// ========================
// Helpers
// ========================

const SLOT_LABELS: Record<string, string> = {
  weapon: 'Silah',
  head: 'Baş',
  chest: 'Gövde',
  gloves: 'Eldiven',
  boots: 'Ayak',
  belt: 'Kemer',
  amulet: 'Muska',
  ring1: 'Yüzük',
  ring2: 'Yüzük',
  cape: 'Pelerin',
  offhand: 'İkinci El',
};

const SLOT_ICONS: Record<string, string> = {
  weapon: '\u2694',
  head: '\u26D1',
  chest: '\u26E8',
  gloves: '\u270B',
  boots: '\u261D',
  belt: '\u25AD',
  amulet: '\u25C7',
  ring1: '\u25CB',
  ring2: '\u25CB',
  cape: '\u2702',
  offhand: '\u26E8',
};

function getItemIcon(item: GameItem): string {
  if ('slot' in item) {
    return SLOT_ICONS[(item as EquipmentItem).slot] || '?';
  }
  if ('itemType' in item) {
    const typed = item as ConsumableItem;
    if (typed.itemType === 'consumable') {
      switch (typed.effect) {
        case 'heal': return '\u2764';
        case 'mana': return '\u2B50';
        case 'buff': return '\u26A1';
      }
    }
  }
  return '?';
}

function formatModType(type: string): string {
  const map: Record<string, string> = {
    maxHp: 'Maks Can',
    maxMana: 'Maks Mana',
    armor: 'Zırh',
    evasion: 'Kaçınma',
    damage: 'Hasar',
    spellDamage: 'Büyü Hasarı',
    energyShield: 'Enerji Kalkanı',
    strength: 'Güç',
    agility: 'Çeviklik',
    spirit: 'Ruh',
    critChance: 'Kritik Şansı',
    moveSpeed: 'Hareket Hızı',
    attackSpeed: 'Saldırı Hızı',
    allResist: 'Tüm Dirençler',
  };
  return map[type] || type;
}

function escapeHTML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ========================
// CSS (injected once)
// ========================

const CSS = `
/* Vendor backdrop */
.vendor-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  color: #f4e8d4;
  user-select: none;
  animation: vendor-fade-in 0.2s ease-out;
}

@keyframes vendor-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* Main panel */
.vendor-panel {
  position: relative;
  width: 860px;
  max-width: 95vw;
  height: 600px;
  max-height: 90vh;
  background: linear-gradient(180deg, rgba(20, 17, 12, 0.97) 0%, rgba(10, 8, 6, 0.98) 100%);
  border: 2px solid #3a3530;
  border-radius: 10px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: vendor-slide-in 0.25s ease-out;
}

@keyframes vendor-slide-in {
  from { transform: translateY(15px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

/* Title bar */
.vendor-title-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  position: relative;
  border-bottom: 1px solid #3a3530;
  flex-shrink: 0;
}

.vendor-title {
  font-size: 16px;
  font-weight: 700;
  color: #d4af37;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  letter-spacing: 0.5px;
}

.vendor-close-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: 1px solid #664444;
  border-radius: 4px;
  background: rgba(80, 40, 40, 0.6);
  color: #aa8888;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}

.vendor-close-btn:hover {
  background: rgba(180, 60, 60, 0.8);
  border-color: #ff6666;
  color: #ffffff;
}

/* Section headers */
.vendor-section-headers {
  display: flex;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(212, 175, 55, 0.15);
}

.vendor-section-header {
  flex: 1;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #d4af37;
  padding: 6px 0;
  letter-spacing: 0.3px;
}

/* Content area */
.vendor-content {
  display: flex;
  flex: 1;
  min-height: 0;
}

/* Panels: shop + player */
.vendor-shop-panel,
.vendor-player-panel {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 6px;
}

.vendor-shop-panel {
  border-right: 1px solid #3a3530;
}

/* Scrollbar styling */
.vendor-shop-panel::-webkit-scrollbar,
.vendor-player-panel::-webkit-scrollbar {
  width: 6px;
}

.vendor-shop-panel::-webkit-scrollbar-track,
.vendor-player-panel::-webkit-scrollbar-track {
  background: rgba(40, 35, 25, 0.8);
}

.vendor-shop-panel::-webkit-scrollbar-thumb,
.vendor-player-panel::-webkit-scrollbar-thumb {
  background: rgba(212, 175, 55, 0.4);
  border-radius: 3px;
}

.vendor-shop-panel::-webkit-scrollbar-thumb:hover,
.vendor-player-panel::-webkit-scrollbar-thumb:hover {
  background: rgba(212, 175, 55, 0.6);
}

/* Item slot */
.vendor-item-slot {
  width: 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.1s;
  position: relative;
}

.vendor-item-slot:hover {
  background: rgba(60, 50, 35, 0.7);
}

.vendor-item-icon {
  width: 48px;
  height: 48px;
  background: rgba(30, 25, 18, 0.9);
  border: 1px solid rgba(80, 70, 50, 0.6);
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  position: relative;
  transition: border-color 0.1s;
}

.vendor-item-slot:hover .vendor-item-icon {
  border-color: #d4af37;
  border-width: 2px;
}

/* Rarity strip at top of icon */
.vendor-item-rarity-strip {
  position: absolute;
  top: 1px;
  left: 1px;
  right: 1px;
  height: 2px;
  border-radius: 1px 1px 0 0;
}

.vendor-item-name {
  font-size: 9px;
  text-align: center;
  margin-top: 2px;
  max-width: 76px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.vendor-item-price {
  font-size: 9px;
  font-weight: 700;
  color: #d4af37;
  margin-top: 1px;
}

/* Empty message */
.vendor-empty-msg {
  width: 100%;
  text-align: center;
  color: #666666;
  font-size: 12px;
  padding-top: 20px;
}

/* Bottom bar */
.vendor-bottom-bar {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-top: 1px solid #3a3530;
  background: rgba(20, 17, 12, 0.9);
  flex-shrink: 0;
  gap: 16px;
}

.vendor-gold-display {
  font-size: 14px;
  font-weight: 700;
  color: #d4af37;
  white-space: nowrap;
}

.vendor-buyback-label {
  font-size: 11px;
  color: #888888;
  white-space: nowrap;
}

.vendor-buyback-slots {
  display: flex;
  gap: 4px;
}

.vendor-buyback-slot {
  width: 44px;
  height: 44px;
  background: rgba(25, 20, 15, 0.8);
  border: 1px solid rgba(60, 50, 35, 0.5);
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  position: relative;
  transition: border-color 0.1s;
}

.vendor-buyback-slot:hover {
  border-color: #d4af37;
  border-width: 2px;
}

.vendor-buyback-slot .vendor-item-rarity-strip {
  position: absolute;
  top: 1px;
  left: 1px;
  right: 1px;
  height: 2px;
}

.vendor-close-hint {
  font-size: 11px;
  color: #888888;
  margin-left: auto;
  white-space: nowrap;
}

/* Feedback messages */
.vendor-feedback-container {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  pointer-events: none;
  z-index: 210;
}

.vendor-feedback-msg {
  font-size: 14px;
  font-weight: 700;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
  animation: vendor-feedback-rise 1.5s ease-out forwards;
  margin-bottom: 4px;
}

@keyframes vendor-feedback-rise {
  0%   { opacity: 1; transform: translateY(0); }
  70%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-40px); }
}

/* Tooltip */
.vendor-tooltip {
  position: fixed;
  z-index: 220;
  pointer-events: none;
  min-width: 200px;
  max-width: 300px;
  background: rgba(16, 14, 10, 0.97);
  border: 1px solid #4a3f2f;
  border-radius: 6px;
  padding: 10px 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.8);
  font-size: 12px;
  line-height: 1.4;
}

.vendor-tooltip-name {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
}

.vendor-tooltip-type {
  font-size: 11px;
  color: #8a8a9a;
  margin-bottom: 2px;
}

.vendor-tooltip-level {
  font-size: 11px;
  color: #8a8a9a;
  margin-bottom: 6px;
}

.vendor-tooltip-separator {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.2), transparent);
  margin: 5px 0;
}

.vendor-tooltip-mod {
  font-size: 11px;
  line-height: 1.5;
}

.vendor-tooltip-mod--implicit {
  color: #c8b080;
}

.vendor-tooltip-mod--prefix,
.vendor-tooltip-mod--suffix {
  color: #6688cc;
}

.vendor-tooltip-consumable {
  font-size: 11px;
  color: #88cc88;
  margin-top: 4px;
}

.vendor-tooltip-price {
  font-size: 12px;
  font-weight: 700;
  color: #d4af37;
  margin-top: 6px;
  padding-top: 4px;
  border-top: 1px solid rgba(212, 175, 55, 0.15);
}
`;

// ========================
// CSS injection
// ========================

let cssInjected = false;
function injectCSS(): void {
  if (cssInjected) return;
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);
  cssInjected = true;
}

// ========================
// VendorHTML class
// ========================

const MAX_BUYBACK = 3;

export class VendorHTML {
  private _isOpen: boolean = false;
  private currentNPC: NPC | null = null;
  private playerInventory: Inventory | null = null;

  // Shop data
  private shopSlots: ShopSlot[] = [];
  private buybackSlots: BuybackSlot[] = [];

  // Feedback
  private feedbackMessages: FeedbackMessage[] = [];

  // DOM
  private backdrop: HTMLDivElement | null = null;
  private shopGrid: HTMLDivElement | null = null;
  private playerGrid: HTMLDivElement | null = null;
  private buybackContainer: HTMLDivElement | null = null;
  private goldDisplay: HTMLDivElement | null = null;
  private feedbackContainer: HTMLDivElement | null = null;
  private tooltipEl: HTMLDivElement | null = null;

  // Keyboard handler
  private boundKeyDown: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    injectCSS();
  }

  // ---- Public API ----

  open(npc: NPC, inventory: Inventory): void {
    this.currentNPC = npc;
    this.playerInventory = inventory;

    // Generate shop inventory
    this.shopSlots = generateShopInventory(npc.type, this.getPlayerLevel());
    this.buybackSlots = [];
    this.feedbackMessages = [];

    this.buildDOM();
    this._isOpen = true;
  }

  close(): void {
    this.destroyDOM();
    this.currentNPC = null;
    this.playerInventory = null;
    this._isOpen = false;
    this.feedbackMessages = [];
  }

  isOpen(): boolean {
    return this._isOpen;
  }

  update(deltaTime: number): void {
    if (!this._isOpen) return;

    // Update feedback timers and remove expired
    for (let i = this.feedbackMessages.length - 1; i >= 0; i--) {
      this.feedbackMessages[i].timer -= deltaTime;
      if (this.feedbackMessages[i].timer <= 0) {
        this.feedbackMessages.splice(i, 1);
      }
    }
  }

  // ---- Helpers ----

  private getPlayerGold(): number {
    return this.playerInventory?.getGold() ?? 0;
  }

  private getPlayerLevel(): number {
    return Math.max(1, Math.min(100, Math.floor(this.getPlayerGold() / 500) + 1));
  }

  // ---- DOM ----

  private buildDOM(): void {
    this.destroyDOM();
    if (!this.currentNPC || !this.playerInventory) return;

    const npc = this.currentNPC;

    // Backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'vendor-backdrop';
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) this.close();
    });

    // Panel
    const panel = document.createElement('div');
    panel.className = 'vendor-panel';

    // Feedback container (above panel visually)
    const feedbackContainer = document.createElement('div');
    feedbackContainer.className = 'vendor-feedback-container';
    panel.appendChild(feedbackContainer);
    this.feedbackContainer = feedbackContainer;

    // Title bar
    const titleBar = document.createElement('div');
    titleBar.className = 'vendor-title-bar';

    const title = document.createElement('div');
    title.className = 'vendor-title';
    title.textContent = `${npc.name} - ${npc.title}`;
    titleBar.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'vendor-close-btn';
    closeBtn.textContent = '\u2715';
    closeBtn.addEventListener('click', () => this.close());
    titleBar.appendChild(closeBtn);

    panel.appendChild(titleBar);

    // Section headers
    const headers = document.createElement('div');
    headers.className = 'vendor-section-headers';

    const shopHeader = document.createElement('div');
    shopHeader.className = 'vendor-section-header';
    shopHeader.textContent = t('shop.forSale');

    const playerHeader = document.createElement('div');
    playerHeader.className = 'vendor-section-header';
    playerHeader.textContent = t('ui.inventory');

    headers.appendChild(shopHeader);
    headers.appendChild(playerHeader);
    panel.appendChild(headers);

    // Content
    const content = document.createElement('div');
    content.className = 'vendor-content';

    // Shop panel (left)
    const shopGrid = document.createElement('div');
    shopGrid.className = 'vendor-shop-panel';
    this.shopGrid = shopGrid;

    // Player panel (right)
    const playerGrid = document.createElement('div');
    playerGrid.className = 'vendor-player-panel';
    this.playerGrid = playerGrid;

    content.appendChild(shopGrid);
    content.appendChild(playerGrid);
    panel.appendChild(content);

    // Bottom bar
    const bottomBar = document.createElement('div');
    bottomBar.className = 'vendor-bottom-bar';

    const goldDisplay = document.createElement('div');
    goldDisplay.className = 'vendor-gold-display';
    this.goldDisplay = goldDisplay;

    const buybackLabel = document.createElement('div');
    buybackLabel.className = 'vendor-buyback-label';
    buybackLabel.textContent = t('shop.buyback');

    const buybackContainer = document.createElement('div');
    buybackContainer.className = 'vendor-buyback-slots';
    this.buybackContainer = buybackContainer;

    const closeHint = document.createElement('div');
    closeHint.className = 'vendor-close-hint';
    closeHint.textContent = t('shop.closeHint');

    bottomBar.appendChild(goldDisplay);
    bottomBar.appendChild(buybackLabel);
    bottomBar.appendChild(buybackContainer);
    bottomBar.appendChild(closeHint);
    panel.appendChild(bottomBar);

    backdrop.appendChild(panel);

    // Tooltip (outside panel so it can overflow)
    const tooltipEl = document.createElement('div');
    tooltipEl.className = 'vendor-tooltip';
    tooltipEl.style.display = 'none';
    backdrop.appendChild(tooltipEl);
    this.tooltipEl = tooltipEl;

    document.body.appendChild(backdrop);
    this.backdrop = backdrop;

    // Populate content
    this.refreshShopGrid();
    this.refreshPlayerGrid();
    this.refreshBuybackSlots();
    this.refreshGold();

    // Keyboard
    this.boundKeyDown = (e: KeyboardEvent) => this.handleKeyDown(e);
    window.addEventListener('keydown', this.boundKeyDown);
  }

  private destroyDOM(): void {
    if (this.boundKeyDown) {
      window.removeEventListener('keydown', this.boundKeyDown);
      this.boundKeyDown = null;
    }
    if (this.backdrop) {
      this.backdrop.remove();
      this.backdrop = null;
    }
    this.shopGrid = null;
    this.playerGrid = null;
    this.buybackContainer = null;
    this.goldDisplay = null;
    this.feedbackContainer = null;
    this.tooltipEl = null;
  }

  // ---- Rendering ----

  private refreshShopGrid(): void {
    if (!this.shopGrid) return;
    this.shopGrid.innerHTML = '';

    if (this.shopSlots.length === 0) {
      const emptyMsg = document.createElement('div');
      emptyMsg.className = 'vendor-empty-msg';
      emptyMsg.textContent = 'Satılık eşya yok';
      this.shopGrid.appendChild(emptyMsg);
      return;
    }

    for (let i = 0; i < this.shopSlots.length; i++) {
      const slot = this.shopSlots[i];
      const el = this.createItemSlotEl(slot.item, slot.price, 'buy', i);
      this.shopGrid.appendChild(el);
    }
  }

  private refreshPlayerGrid(): void {
    if (!this.playerGrid || !this.playerInventory) return;
    this.playerGrid.innerHTML = '';

    const items = this.playerInventory.getAllItems();
    let hasItems = false;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      hasItems = true;
      const sellPrice = calculateSellPrice(item);
      const el = this.createItemSlotEl(item, sellPrice, 'sell', i);
      this.playerGrid.appendChild(el);
    }

    if (!hasItems) {
      const emptyMsg = document.createElement('div');
      emptyMsg.className = 'vendor-empty-msg';
      emptyMsg.textContent = t('shop.inventoryEmpty');
      this.playerGrid.appendChild(emptyMsg);
    }
  }

  private refreshBuybackSlots(): void {
    if (!this.buybackContainer) return;
    this.buybackContainer.innerHTML = '';

    for (let i = 0; i < MAX_BUYBACK; i++) {
      const slotEl = document.createElement('div');
      slotEl.className = 'vendor-buyback-slot';

      const bbSlot = this.buybackSlots[i];
      if (bbSlot) {
        const rarityColor = getItemColor(bbSlot.item.rarity);

        // Rarity strip
        const strip = document.createElement('div');
        strip.className = 'vendor-item-rarity-strip';
        strip.style.background = rarityColor;
        slotEl.appendChild(strip);

        // Icon
        const icon = document.createTextNode(getItemIcon(bbSlot.item));
        slotEl.style.color = rarityColor;
        slotEl.appendChild(icon);

        // Click to buyback
        slotEl.addEventListener('click', () => this.tryBuyback(i));

        // Tooltip
        slotEl.addEventListener('mouseenter', (e) => {
          this.showTooltip(bbSlot.item, bbSlot.price, 'buyback', e);
        });
        slotEl.addEventListener('mousemove', (e) => {
          this.positionTooltip(e);
        });
        slotEl.addEventListener('mouseleave', () => {
          this.hideTooltip();
        });
      }

      this.buybackContainer.appendChild(slotEl);
    }
  }

  private refreshGold(): void {
    if (!this.goldDisplay) return;
    this.goldDisplay.textContent = `${t('ui.gold')}: ${this.getPlayerGold()}`;
  }

  private createItemSlotEl(item: GameItem, price: number, mode: 'buy' | 'sell', index: number): HTMLDivElement {
    const rarityColor = getItemColor(item.rarity);

    const slot = document.createElement('div');
    slot.className = 'vendor-item-slot';

    // Icon container
    const iconBox = document.createElement('div');
    iconBox.className = 'vendor-item-icon';

    // Rarity strip
    const strip = document.createElement('div');
    strip.className = 'vendor-item-rarity-strip';
    strip.style.background = rarityColor;
    iconBox.appendChild(strip);

    // Icon character
    const iconSpan = document.createElement('span');
    iconSpan.textContent = getItemIcon(item);
    iconSpan.style.color = rarityColor;
    iconBox.appendChild(iconSpan);

    slot.appendChild(iconBox);

    // Item name
    const nameEl = document.createElement('div');
    nameEl.className = 'vendor-item-name';
    nameEl.textContent = item.name;
    nameEl.style.color = rarityColor;
    slot.appendChild(nameEl);

    // Price
    const priceEl = document.createElement('div');
    priceEl.className = 'vendor-item-price';
    priceEl.textContent = `${price}g`;
    slot.appendChild(priceEl);

    // Click: buy or sell
    slot.addEventListener('click', () => {
      if (mode === 'buy') {
        this.tryBuy(index);
      } else {
        this.trySell(index);
      }
    });

    // Tooltip
    slot.addEventListener('mouseenter', (e) => {
      this.showTooltip(item, price, mode, e);
    });
    slot.addEventListener('mousemove', (e) => {
      this.positionTooltip(e);
    });
    slot.addEventListener('mouseleave', () => {
      this.hideTooltip();
    });

    return slot;
  }

  // ---- Tooltip ----

  private showTooltip(item: GameItem, price: number, mode: 'buy' | 'sell' | 'buyback', e: MouseEvent): void {
    if (!this.tooltipEl) return;

    let html = '';

    const rarityColor = getItemColor(item.rarity);

    // Name
    html += `<div class="vendor-tooltip-name" style="color:${rarityColor}">${escapeHTML(item.name)}</div>`;

    // Type/slot
    if ('slot' in item) {
      const equip = item as EquipmentItem;
      const slotName = SLOT_LABELS[equip.slot] || equip.slot;
      html += `<div class="vendor-tooltip-type">${escapeHTML(slotName)}</div>`;
    } else if ('itemType' in item) {
      const typed = item as ConsumableItem;
      if (typed.itemType === 'consumable') {
        html += `<div class="vendor-tooltip-type">Tüketilebilir</div>`;
      }
    }

    // Level
    html += `<div class="vendor-tooltip-level">Seviye ${item.level}</div>`;

    // Equipment mods
    if ('slot' in item) {
      const equip = item as EquipmentItem;

      // Implicits
      if (equip.implicit && equip.implicit.length > 0) {
        html += '<div class="vendor-tooltip-separator"></div>';
        for (const mod of equip.implicit) {
          const sign = mod.value >= 0 ? '+' : '';
          const pct = mod.calcType === 'increased' || mod.calcType === 'more' ? '%' : '';
          html += `<div class="vendor-tooltip-mod vendor-tooltip-mod--implicit">${sign}${mod.value}${pct} ${escapeHTML(formatModType(mod.type))}</div>`;
        }
      }

      // Prefixes
      if (equip.prefixes && equip.prefixes.length > 0) {
        html += '<div class="vendor-tooltip-separator"></div>';
        for (const mod of equip.prefixes) {
          const sign = mod.value >= 0 ? '+' : '';
          const pct = mod.calcType === 'increased' || mod.calcType === 'more' ? '%' : '';
          html += `<div class="vendor-tooltip-mod vendor-tooltip-mod--prefix">${sign}${mod.value}${pct} ${escapeHTML(formatModType(mod.type))}</div>`;
        }
      }

      // Suffixes
      if (equip.suffixes && equip.suffixes.length > 0) {
        if (!equip.prefixes || equip.prefixes.length === 0) {
          html += '<div class="vendor-tooltip-separator"></div>';
        }
        for (const mod of equip.suffixes) {
          const sign = mod.value >= 0 ? '+' : '';
          const pct = mod.calcType === 'increased' || mod.calcType === 'more' ? '%' : '';
          html += `<div class="vendor-tooltip-mod vendor-tooltip-mod--suffix">${sign}${mod.value}${pct} ${escapeHTML(formatModType(mod.type))}</div>`;
        }
      }
    }

    // Consumable details
    if ('itemType' in item) {
      const typed = item as ConsumableItem;
      if (typed.itemType === 'consumable') {
        html += '<div class="vendor-tooltip-separator"></div>';
        let effectText = '';
        switch (typed.effect) {
          case 'heal': effectText = `+${typed.value} Can`; break;
          case 'mana': effectText = `+${typed.value} Mana`; break;
          case 'buff': effectText = `+${typed.value} Güç (${typed.duration}s)`; break;
        }
        html += `<div class="vendor-tooltip-consumable">${effectText}</div>`;
      }
    }

    // Price
    const priceLabel = mode === 'sell' ? 'Satış' : (mode === 'buyback' ? 'Geri Al' : 'Fiyat');
    html += `<div class="vendor-tooltip-price">${priceLabel}: ${price}g</div>`;

    this.tooltipEl.innerHTML = html;
    this.tooltipEl.style.display = 'block';
    this.positionTooltip(e);
  }

  private positionTooltip(e: MouseEvent): void {
    if (!this.tooltipEl) return;

    const pad = 12;
    let x = e.clientX + pad;
    let y = e.clientY + pad;

    const rect = this.tooltipEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (x + rect.width > vw - pad) {
      x = e.clientX - rect.width - pad;
    }
    if (y + rect.height > vh - pad) {
      y = e.clientY - rect.height - pad;
    }

    this.tooltipEl.style.left = `${x}px`;
    this.tooltipEl.style.top = `${y}px`;
  }

  private hideTooltip(): void {
    if (this.tooltipEl) {
      this.tooltipEl.style.display = 'none';
    }
  }

  // ---- Transactions ----

  private tryBuy(shopIndex: number): void {
    if (!this.playerInventory) return;
    if (shopIndex < 0 || shopIndex >= this.shopSlots.length) return;

    const slot = this.shopSlots[shopIndex];

    if (this.getPlayerGold() < slot.price) {
      this.addFeedback(t('shop.insufficientGold'), '#d45c5c');
      return;
    }

    if (this.playerInventory.isFull()) {
      this.addFeedback(t('shop.inventoryFull'), '#d45c5c');
      return;
    }

    // Deduct gold
    this.playerInventory.removeGold(slot.price);

    // Copy item to player
    const boughtItem = { ...slot.item, id: generateShopItemId() };
    this.playerInventory.addItem(boughtItem);

    // Remove from shop
    this.shopSlots.splice(shopIndex, 1);

    this.addFeedback(`-${slot.price} ${t('ui.gold')}`, '#d4af37');
    this.hideTooltip();
    this.refreshAll();
  }

  private trySell(inventoryIndex: number): void {
    if (!this.playerInventory) return;

    const item = this.playerInventory.getItem(inventoryIndex);
    if (!item) return;

    // Don't sell quest items
    if ('itemType' in item && (item as { itemType: string }).itemType === 'quest') {
      this.addFeedback(t('ui.questItemNoSell'), '#d45c5c');
      return;
    }

    const sellPrice = calculateSellPrice(item);

    // Remove from inventory
    this.playerInventory.removeItem(inventoryIndex);

    // Add gold
    this.playerInventory.addGold(sellPrice);

    // Add to buyback
    this.buybackSlots.push({ item, price: sellPrice });
    if (this.buybackSlots.length > MAX_BUYBACK) {
      this.buybackSlots.shift();
    }

    this.addFeedback(`+${sellPrice} ${t('ui.gold')}`, '#5cd47a');
    this.hideTooltip();
    this.refreshAll();
  }

  private tryBuyback(buybackIndex: number): void {
    if (!this.playerInventory) return;
    if (buybackIndex < 0 || buybackIndex >= this.buybackSlots.length) return;

    const bbSlot = this.buybackSlots[buybackIndex];

    if (this.getPlayerGold() < bbSlot.price) {
      this.addFeedback(t('shop.insufficientGold'), '#d45c5c');
      return;
    }

    if (this.playerInventory.isFull()) {
      this.addFeedback(t('shop.inventoryFull'), '#d45c5c');
      return;
    }

    // Deduct gold
    this.playerInventory.removeGold(bbSlot.price);
    this.playerInventory.addItem(bbSlot.item);

    // Remove from buyback
    this.buybackSlots.splice(buybackIndex, 1);

    this.addFeedback(`-${bbSlot.price} ${t('ui.gold')} (${t('shop.buyback')})`, '#d4af37');
    this.hideTooltip();
    this.refreshAll();
  }

  // ---- Feedback ----

  private addFeedback(text: string, color: string): void {
    this.feedbackMessages.push({ text, color, timer: 1.5 });
    if (this.feedbackMessages.length > 5) {
      this.feedbackMessages.shift();
    }

    // Render feedback message as a DOM element with animation
    if (this.feedbackContainer) {
      const msgEl = document.createElement('div');
      msgEl.className = 'vendor-feedback-msg';
      msgEl.textContent = text;
      msgEl.style.color = color;
      this.feedbackContainer.appendChild(msgEl);

      // Remove after animation completes
      setTimeout(() => {
        msgEl.remove();
      }, 1500);
    }
  }

  // ---- Refresh all sections ----

  private refreshAll(): void {
    this.refreshShopGrid();
    this.refreshPlayerGrid();
    this.refreshBuybackSlots();
    this.refreshGold();
  }

  // ---- Keyboard ----

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this._isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  }
}
